import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState, getClient } from '@src/core/store';
import { AgreementDocument, AppInfo } from '@src/core/buyerApiClient';

export type allowedCurrentTerms = 'terms_of_use' | 'privacy_policy' | null;

interface AppState {
  publicApiKey?: string;
  termsData?: { [key: string]: AgreementDocument };
  loadingTerms: boolean;
  currentTerms: allowedCurrentTerms;
  appInfo?: AppInfo;
  appInfoLoaded: boolean;
}

const initialState: AppState = {
  currentTerms: null,
  loadingTerms: false,
  appInfoLoaded: false,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setPublicKey: (state: AppState, action: PayloadAction<string, string>) => {
      state.publicApiKey = action.payload;
    },
    setTermsData: (state: AppState, action: PayloadAction<{ [key: string]: AgreementDocument }, string>) => {
      state.termsData = action.payload;
    },
    setCurrentTerms: (state: AppState, action: PayloadAction<allowedCurrentTerms>) => {
      state.currentTerms = action.payload;
    },
    setLoadingTerms: (state: AppState, action: PayloadAction<boolean>) => {
      state.loadingTerms = action.payload;
    },
    setAppInfoResult: (state: AppState, action: PayloadAction<AppInfo | undefined>) => {
      state.appInfoLoaded = true;
      state.appInfo = action.payload;
    },
  },
});

export default appSlice.reducer;

export const { setPublicKey, setTermsData, setCurrentTerms, setLoadingTerms, setAppInfoResult } = appSlice.actions;

export const loadAppInfo = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  try {
    const resp = await getClient(state).apps.getAppInfo();
    dispatch(setAppInfoResult(resp.data));
  } catch {
    dispatch(setAppInfoResult(undefined));
  }
};

export const loadTerms = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    app: { termsData },
  } = state;

  if (termsData) {
    return;
  }

  dispatch(setLoadingTerms(true));

  const client = getClient(state);
  const { data: termsOfUseDoc } = await client.buyers.getTermsOfUse();
  const { data: privacyPolicyDoc } = await client.buyers.getPrivacyPolicy();

  dispatch(
    setTermsData({
      terms_of_use: termsOfUseDoc,
      privacy_policy: privacyPolicyDoc,
    }),
  );

  dispatch(setLoadingTerms(false));

  dispatch(setCurrentTerms(null));
};

export const publicApiTokenSelector = (state: RootState) => state.app.publicApiKey!;
export const publicApiKeySelector = (state: RootState) => state.app.publicApiKey;
export const termsSelector = (state: RootState) => state.app.termsData;
export const loadingTermsSelector = (state: RootState) => state.app.loadingTerms;
export const currentTermsSelector = (state: RootState) => state.app.currentTerms;
export const appInfoSelector = (state: RootState) => state.app.appInfo;
export const appInfoLoadedSelector = (state: RootState) => state.app.appInfoLoaded;

export const featureFlagsSelector = () => (state: RootState) => state.app.appInfo?.featureFlags;
export const featureFlagSelector = (flag: string) => (state: RootState) =>
  state.app.appInfo?.featureFlags?.[flag] === true;
